/**
 * FORQ Signal — the task center (spec 2.2). View model for the shell region that turns the
 * `public.tasks` backend (migration 20260713063300) into the actionable "what needs your
 * attention" list. Categories mirror the spec's task types
 * (abrechnen/prüfen/freigeben/UVV/nachverfolgen/rücknahme/Ersatzteil-fehlt).
 *
 * A Supabase implementation maps `tasks` rows → SignalTask: `route` derived from
 * source_module/source_entity_*, `category` from the task's origin, `requiredPermission`
 * from the action the task represents. The demo gateway supplies the same shape offline.
 */
export type SignalCategory =
  | 'billing' // abrechnen
  | 'review' // prüfen
  | 'approval' // freigeben
  | 'inspection' // UVV / Prüfung
  | 'follow_up' // nachverfolgen
  | 'return' // rücknahme
  | 'spare_part'; // Ersatzteil fehlt

/** Same scale as the `tasks.priority` check constraint. */
export type SignalPriority = 'low' | 'normal' | 'high' | 'urgent';

export interface SignalTask {
  readonly id: string;
  readonly tenantId: string;
  readonly category: SignalCategory;
  readonly title: string;
  readonly detail: string;
  readonly priority: SignalPriority;
  /** ISO date; null when the task has no deadline. */
  readonly dueAt: string | null;
  /** Owning module — shown as a source chip. */
  readonly sourceModule: string;
  /** Deep link into the source record/section (German record-URLs). */
  readonly route: string;
  /** Permission required to act on the task; the center only shows tasks the user may action. */
  readonly requiredPermission: string;
}

export interface SignalListInput {
  readonly tenantId: string;
  readonly permissions: ReadonlySet<string>;
}

export interface SignalCompleteInput {
  readonly tenantId: string;
  readonly taskId: string;
  readonly permissions: ReadonlySet<string>;
}

export interface SignalGateway {
  /** Open tasks for the active tenant that the user is permitted to action, most urgent first. */
  listOpenTasks(input: SignalListInput): Promise<readonly SignalTask[]>;
  /** Marks a task done and returns the refreshed open list for the same tenant/permissions. */
  completeTask(input: SignalCompleteInput): Promise<readonly SignalTask[]>;
}

const priorityRank: Readonly<Record<SignalPriority, number>> = {
  urgent: 0,
  high: 1,
  normal: 2,
  low: 3,
};

/** Local calendar date as `YYYY-MM-DD`. Day granularity avoids flagging today's tasks as overdue. */
export function todayIsoDate(now: Date = new Date()): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Overdue first, then by priority, then by earliest due date — the center's canonical order. */
export function compareSignalTasks(left: SignalTask, right: SignalTask, todayIso: string): number {
  const leftOverdue = left.dueAt !== null && left.dueAt < todayIso;
  const rightOverdue = right.dueAt !== null && right.dueAt < todayIso;
  if (leftOverdue !== rightOverdue) return leftOverdue ? -1 : 1;
  if (priorityRank[left.priority] !== priorityRank[right.priority]) {
    return priorityRank[left.priority] - priorityRank[right.priority];
  }
  return (left.dueAt ?? '9999-12-31').localeCompare(right.dueAt ?? '9999-12-31');
}

export type SignalDueTone = 'overdue' | 'today' | 'soon' | 'later' | 'none';

export interface SignalDueDescription {
  readonly label: string;
  readonly tone: SignalDueTone;
}

/** Human, tenant-language due label relative to `todayIso` (both date-only `YYYY-MM-DD`). */
export function describeDue(dueAt: string | null, todayIso: string): SignalDueDescription {
  if (!dueAt) return { label: 'ohne Frist', tone: 'none' };
  if (dueAt < todayIso) return { label: 'überfällig', tone: 'overdue' };
  if (dueAt === todayIso) return { label: 'heute fällig', tone: 'today' };
  const oneDay = 86_400_000;
  const days = Math.round((Date.parse(dueAt) - Date.parse(todayIso)) / oneDay);
  if (days === 1) return { label: 'morgen fällig', tone: 'soon' };
  if (days <= 6) return { label: `in ${days} Tagen`, tone: 'soon' };
  const [year, month, day] = dueAt.split('-');
  return { label: `am ${day}.${month}.${year}`, tone: 'later' };
}
